import { useEffect, useState, type KeyboardEvent, type ReactElement } from 'react';
import { Alert, Badge, Button, Checkbox, Group, Loader, Modal, Paper, ScrollArea, Stack, Text, TextInput } from '@mantine/core';
import { Sparkles } from 'lucide-react';
import type { CategorySuggestion } from '@/shared/assist/CategoryAssistContract';
import type { UsageCategoriesViewModel } from './UsageCategoriesViewModel';

const PRESETS: string[] = [
    'Add subcategories for the main types of work',
    'Break this up into subcategories by how it is used',
    'Rename this to something clearer',
];

interface UsageCategoryAssistModalProps {
    vm: UsageCategoriesViewModel;
    onApply: () => void;
}

/**
 * Per-category AI assist surface scoped to one category: the user describes what they
 * want and the assistant infers the action (add / split / rename) from the prompt and
 * applies it to this category. Proposed changes are shown as selectable cards where
 * each label can be edited inline; only the selected suggestions are staged into the
 * editable draft. Nothing is written to the server until the page is saved.
 */
export default function UsageCategoryAssistModal({ vm, onApply }: UsageCategoryAssistModalProps): ReactElement {
    const [prompt, setPrompt] = useState('');
    const opened = vm.assistModalDraftId !== null;
    const target = vm.selectedCategory;

    useEffect(() => {
        if (opened) setPrompt('');
    }, [opened, vm.assistModalDraftId]);

    const ready = vm.canGenerate && prompt.trim().length > 0;
    const generate = (): void => {
        if (ready) void vm.generateSuggestions(prompt.trim());
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === 'Enter') {
            event.preventDefault();
            generate();
        }
    };

    const hasSuggestions = vm.suggestions.length > 0;
    const selectedCount = vm.selectedSuggestionIndices.size;
    const allSelected = hasSuggestions && selectedCount === vm.suggestions.length;
    const someSelected = selectedCount > 0 && !allSelected;

    return (
        <Modal opened={opened} onClose={() => vm.closeAssist()} title={target ? `AI assist — ${target.label}` : 'AI assist'} size="xl" centered>
            <Stack gap="md">
                <Text size="sm" c="dimmed">
                    Describe what you want for this category — add subcategories, split it up, or rename it — and the assistant figures out the rest.
                    Suggestions are grounded in your usage data and staged for your review; nothing changes until you Save.
                </Text>

                <Group align="flex-end" gap="sm" wrap="nowrap">
                    <TextInput
                        style={{ flex: 1 }}
                        value={prompt}
                        onChange={(event) => setPrompt(event.currentTarget.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="What would you like to do with this category?"
                        leftSection={<Sparkles size={15} />}
                        aria-label="Assist prompt"
                        data-autofocus
                    />
                    <Button leftSection={<Sparkles size={15} />} loading={vm.generating} disabled={!ready} onClick={generate}>
                        Generate
                    </Button>
                </Group>

                <Group gap={6}>
                    {PRESETS.map((preset) => (
                        <Button key={preset} size="xs" variant="light" color="gray" radius="xl" onClick={() => setPrompt(preset)}>
                            {preset}
                        </Button>
                    ))}
                </Group>

                {vm.assistError !== null && (
                    <Alert color="red" variant="light">
                        {vm.assistError}
                    </Alert>
                )}

                {vm.generating && (
                    <Group gap="xs" justify="center" py="md">
                        <Loader size="sm" />
                        <Text size="sm" c="dimmed">
                            Looking at your usage…
                        </Text>
                    </Group>
                )}

                {!vm.generating && vm.hasGenerated && !hasSuggestions && vm.assistError === null && (
                    <Alert color="blue" variant="light" title="No changes to apply">
                        <Stack gap={6}>
                            {vm.assistNarrative.length > 0 && <Text size="sm">{vm.assistNarrative}</Text>}
                            <Text size="sm" c="dimmed">
                                Try being more specific about what you want — for example “add subcategories for sourcing and screening”, “split this into
                                subcategories by topic”, or “rename this to something clearer”.
                            </Text>
                        </Stack>
                    </Alert>
                )}

                {!vm.generating && hasSuggestions && (
                    <Stack gap="sm">
                        {vm.assistNarrative.length > 0 && <Text size="sm">{vm.assistNarrative}</Text>}
                        <Group justify="space-between">
                            <Checkbox
                                checked={allSelected}
                                indeterminate={someSelected}
                                onChange={(event) => vm.setAllSuggestionsSelected(event.currentTarget.checked)}
                                label="Select all"
                            />
                            <Text size="xs" c="dimmed">
                                {selectedCount} of {vm.suggestions.length} selected
                            </Text>
                        </Group>
                        <ScrollArea.Autosize mah={380} type="auto" offsetScrollbars="y" pr={4} pb={2}>
                            <Stack gap="xs" my="xs">
                                {vm.suggestions.map((suggestion, index) => (
                                    <SuggestionCard key={index} vm={vm} index={index} suggestion={suggestion} />
                                ))}
                            </Stack>
                        </ScrollArea.Autosize>
                    </Stack>
                )}

                {hasSuggestions && (
                    <Group justify="flex-end" gap="sm" mt="sm">
                        <Button variant="default" onClick={() => vm.closeAssist()}>
                            Cancel
                        </Button>
                        <Button leftSection={<Sparkles size={15} />} disabled={!vm.canApplySuggestions} onClick={onApply}>
                            Apply{selectedCount > 0 ? ` (${selectedCount})` : ''}
                        </Button>
                    </Group>
                )}
            </Stack>
        </Modal>
    );
}

function SuggestionCard({ vm, index, suggestion }: { vm: UsageCategoriesViewModel; index: number; suggestion: CategorySuggestion }): ReactElement {
    const selected = vm.selectedSuggestionIndices.has(index);
    const isSplit = suggestion.kind === 'split-category';
    return (
        <Paper withBorder radius="md" p="sm">
            <Group align="flex-start" wrap="nowrap" gap="sm">
                <Checkbox mt={4} checked={selected} onChange={() => vm.toggleSuggestion(index)} aria-label={`Select ${kindTitle(suggestion)} suggestion`} />
                <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
                    <Group gap={8} wrap="nowrap" align={isSplit ? 'flex-start' : 'center'}>
                        <Badge size="sm" variant="light" color="violet" style={{ flexShrink: 0 }}>
                            {kindTitle(suggestion)}
                        </Badge>
                        <ChangeEditor vm={vm} index={index} suggestion={suggestion} />
                    </Group>
                    {suggestion.rationale.length > 0 && (
                        <Text size="sm" c="dimmed">
                            {suggestion.rationale}
                        </Text>
                    )}
                    <Evidence suggestion={suggestion} />
                </Stack>
            </Group>
        </Paper>
    );
}

function ChangeEditor({ vm, index, suggestion }: { vm: UsageCategoriesViewModel; index: number; suggestion: CategorySuggestion }): ReactElement {
    if (suggestion.kind === 'split-category') {
        return (
            <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                {suggestion.newSubcategories.map((sub, subIndex) => (
                    <TextInput
                        key={subIndex}
                        size="xs"
                        value={sub}
                        onChange={(event) => vm.setSplitSubcategory(index, subIndex, event.currentTarget.value)}
                        aria-label={`Subcategory ${subIndex + 1}`}
                    />
                ))}
            </Stack>
        );
    }
    const value = suggestion.kind === 'add-subcategory' ? suggestion.label : suggestion.newLabel;
    return (
        <TextInput
            size="xs"
            style={{ flex: 1, minWidth: 0 }}
            value={value}
            onChange={(event) => vm.setSuggestionLabel(index, event.currentTarget.value)}
            aria-label={suggestion.kind === 'add-subcategory' ? 'New subcategory label' : 'New label'}
        />
    );
}

function Evidence({ suggestion }: { suggestion: CategorySuggestion }): ReactElement | null {
    const evidence = suggestion.evidence;
    if (!evidence) return null;
    const topics = evidence.topTopics ?? [];
    if (evidence.assignmentCount === undefined && topics.length === 0) return null;
    return (
        <Group gap={6}>
            {evidence.assignmentCount !== undefined && (
                <Badge size="xs" variant="outline" color="gray">
                    {evidence.assignmentCount.toLocaleString()} assignments
                </Badge>
            )}
            {topics.map((topic) => (
                <Badge key={topic} size="xs" variant="dot" color="blue">
                    {topic}
                </Badge>
            ))}
        </Group>
    );
}

function kindTitle(suggestion: CategorySuggestion): string {
    switch (suggestion.kind) {
        case 'add-subcategory':
            return 'Add';
        case 'split-category':
            return 'Split';
        case 'rename':
            return 'Rename';
    }
}
